// Steps/ReviewAndSend.js - Step 4 Component
import React, { useContext } from 'react';
import { Box, Typography, Grid, TextField, Paper, Chip, Card, CardContent, Divider, Alert, List, ListItem, ListItemIcon, ListItemText, Avatar} from '@mui/material';
import { People, Engineering, Schedule, NotificationsActive, Sms, WhatsApp, Email, Title, Description, CheckCircle} from '@mui/icons-material';
import dayjs from 'dayjs';
import { NotificationDataContext } from '../AddEditNotification';

const ReviewAndSend = ({ onSubmit }) => {
    const { notificationData, setNotificationData } = useContext(NotificationDataContext);

    const selectedAudience = notificationData.audienceType === 'customer' 
        ? notificationData.customerFilters.selectedCustomers 
        : notificationData.expertFilters.selectedExperts;

    const handleTitleChange = (event) => {
        setNotificationData(prev => ({
            ...prev,
            title: event.target.value
        }));
    };

    const handleBodyChange = (event) => {
        setNotificationData(prev => ({
            ...prev,
            body: event.target.value
        }));
    };

    const getDeliveryModeIcon = (mode) => {
        switch (mode) {
            case 'push': return <NotificationsActive sx={{ fontSize: 16 }} />;
            case 'sms': return <Sms sx={{ fontSize: 16 }} />;
            case 'whatsapp': return <WhatsApp sx={{ fontSize: 16 }} />;
            case 'email': return <Email sx={{ fontSize: 16 }} />;
            default: return <NotificationsActive sx={{ fontSize: 16 }} />;
        }
    };

    const getDeliveryModeLabel = (mode) => {
        switch (mode) {
            case 'push': return 'Push Notification';
            case 'sms': return 'SMS';
            case 'whatsapp': return 'WhatsApp';
            case 'email': return 'Email';
            default: return 'Unknown';
        }
    };

    const isFormValid = notificationData.title.trim() !== '' && 
                       notificationData.body.trim() !== '' &&
                       selectedAudience.length > 0 &&
                       notificationData.deliveryModes.length > 0 &&
                       (notificationData.scheduleType === 'now' || 
                        (notificationData.scheduleType === 'later' && notificationData.scheduledDateTime));

    return (
        <Box>
            <Typography gutterBottom sx={{ mb: 1, fontWeight: 600 ,fontSize:'0.85rem'}}>
                Review & Send Notification
            </Typography>

            <Grid container spacing={3}>
                {/* Summary Panel */}
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom sx={{ 
                                fontWeight: 600, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                mb: 2
                            }}>
                                <CheckCircle color="primary" />
                                Configuration Summary
                            </Typography>

                            <List dense>
                                {/* Audience Summary */}
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {notificationData.audienceType === 'customer' ? 
                                            <People color="primary" fontSize="small" /> : 
                                            <Engineering color="primary" fontSize="small" />
                                        }
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                Target Audience
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                {selectedAudience.length} {notificationData.audienceType}(s)
                                            </Typography>
                                        }
                                    />
                                </ListItem>

                                {/* Schedule Summary */}
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Schedule color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                Schedule
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                {notificationData.scheduleType === 'now' ? 'Send immediately' :
                                                    notificationData.scheduledDateTime ?
                                                        dayjs(notificationData.scheduledDateTime).format('MMM DD, hh:mm A') :
                                                        'Not scheduled'
                                                }
                                            </Typography>
                                        }
                                    />
                                </ListItem>

                                {/* Delivery Modes */}
                                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <NotificationsActive color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                Delivery Channels
                                            </Typography>
                                        }
                                        secondary={
                                            <Box sx={{ mt: 0.5 }}>
                                                {notificationData.deliveryModes.map((mode, index) => (
                                                    <Chip
                                                        key={mode}
                                                        icon={getDeliveryModeIcon(mode)}
                                                        label={getDeliveryModeLabel(mode)}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            height: 20,
                                                            mr: 0.5,
                                                            mb: 0.5,
                                                            bgcolor: 'primary.100',
                                                            color: 'primary.800'
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Message Composition */}
                <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card variant="outlined">
                                <CardContent>
                                    {notificationData.title && notificationData.body && (
                                        <Box sx={{}}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                                Preview:
                                            </Typography>
                                            <Paper 
                                                sx={{ 
                                                    p: 2, 
                                                    bgcolor: 'grey.50', 
                                                    border: '1px dashed', 
                                                    borderColor: 'grey.300',
                                                    // maxWidth: 300
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                                                        <NotificationsActive sx={{ fontSize: 14 }} />
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ 
                                                            fontWeight: 600, 
                                                            fontSize: '0.8rem',
                                                            lineHeight: 1.2,
                                                            mb: 0.5
                                                        }}>
                                                            {notificationData.title}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ 
                                                            fontSize: '0.7rem',
                                                            color: 'text.secondary',
                                                            lineHeight: 1.3
                                                        }}>
                                                            {notificationData.body}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            {/* Selected Recipients Preview */}
                            {selectedAudience.length > 0 && (
                                <Card variant="outlined">
                                <CardContent>
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom sx={{ 
                                        fontWeight: 600, 
                                        fontSize: '0.8rem',
                                        mb: 1
                                    }}>
                                        Selected Recipients ({selectedAudience.length}):
                                    </Typography>
                                    <Box sx={{ maxHeight: 150, overflowY: 'auto', px: 1 }}>
                                        {selectedAudience.slice(0, 5).map((recipient, index) => (
                                            <Chip
                                                key={index}
                                                label={`ID: ${recipient.id}`}
                                                size="small"
                                                sx={{
                                                    fontSize: '0.6rem',
                                                    height: 18,
                                                    mr: 0.5,
                                                    mb: 0.5,
                                                    bgcolor: 'grey.100'
                                                }}
                                            />
                                        ))}
                                        {selectedAudience.length > 5 && (
                                            <Typography variant="caption" sx={{ 
                                                fontSize: '0.65rem',
                                                color: 'text.secondary',
                                                fontStyle: 'italic'
                                            }}>
                                                +{selectedAudience.length - 5} more...
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                </CardContent>
                                </Card>
                            )}
                        </Grid>
                        
                    </Grid>   
                </Grid>
                {/* Final Confirmation */}
                <Grid item xs={12} md={4}>
                    
                    {isFormValid && (
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Final Review:
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    You are about to send "<strong>{notificationData.title}</strong>" to{' '}
                                    <strong>{selectedAudience.length}</strong> {notificationData.audienceType}(s) via{' '}
                                    <strong>{notificationData.deliveryModes.map(mode => getDeliveryModeLabel(mode)).join(', ')}</strong>.
                                </Typography>
                                {notificationData.scheduleType === 'later' && (
                                    <Typography variant="body2">
                                        The notification will be delivered on{' '}
                                        <strong>{dayjs(notificationData.scheduledDateTime).format('MMMM DD, YYYY at hh:mm A')}</strong>.
                                    </Typography>
                                )}
                            </Alert>
                        </Grid>
                    )}
                    

                    {/* Validation Status */}
                    <Box sx={{ mt: 2 }}>
                        {isFormValid ? (
                            <Alert severity="success" sx={{ fontSize: '0.75rem' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    Ready to Send! ✅
                                </Typography>
                                All required fields are completed. You can proceed to send the notification.
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    Missing Information ⚠️
                                </Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                                    {!notificationData.title.trim() && <li>Title is required</li>}
                                    {!notificationData.body.trim() && <li>Message body is required</li>}
                                    {selectedAudience.length === 0 && <li>Select at least one recipient</li>}
                                    {notificationData.scheduleType === 'later' && !notificationData.scheduledDateTime && 
                                        <li>Schedule date & time is required</li>
                                    }
                                </Box>
                            </Alert>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ReviewAndSend;